import CodeMirror from '@uiw/react-codemirror';
import { yaml } from '@codemirror/lang-yaml';
import { oneDark } from '@codemirror/theme-one-dark';
import { EditorView, Decoration, DecorationSet } from '@codemirror/view';
import { StateField, StateEffect } from '@codemirror/state';
import { useAtomValue } from 'jotai';
import { useRef, useEffect, useCallback } from 'react';
import { siteThemeMode } from '~/atoms/userPreferencesAtoms';
import { ReactCodeMirrorRef } from '@uiw/react-codemirror';

interface YamlEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: string;
  readOnly?: boolean;
  hasError?: boolean;
  errorLine?: number | null;
}

// Effect to set the error line
const setErrorLineEffect = StateEffect.define<number | null>();

// Line decoration for error highlighting
const errorLineDeco = Decoration.line({ class: 'cm-errorLine' });

// StateField to track error line decoration
const errorLineField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none;
  },
  update(decorations, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setErrorLineEffect)) {
        if (effect.value === null) {
          return Decoration.none;
        }
        const line = tr.state.doc.line(
          Math.min(effect.value, tr.state.doc.lines),
        );
        return Decoration.set([errorLineDeco.range(line.from)]);
      }
    }
    return decorations.map(tr.changes);
  },
  provide: (f) => EditorView.decorations.from(f),
});

const darkBaseTheme = EditorView.theme({
  '&': {
    backgroundColor: 'var(--bq-surface-base, #1a1a2e)',
    borderRadius: '6px',
    border: '1px solid var(--bq-border-muted, #3f3f5a)',
    fontSize: '13px',
    flex: '1',
    minHeight: '0',
    maxHeight: '100%',
    overflow: 'hidden',
  },
  '&.cm-editor': {
    height: '100%',
  },
  '&.cm-focused': {
    outline: 'none',
    borderColor: 'var(--bq-color-primary, #6366f1)',
  },
  '.cm-scroller': {
    fontFamily: '"JetBrains Mono", "Fira Code", "Monaco", monospace',
    lineHeight: '1.6',
    overflow: 'auto',
  },
  '.cm-gutters': {
    backgroundColor: 'var(--bq-surface-elevated, #252538)',
    borderRight: '1px solid var(--bq-border-muted, #3f3f5a)',
    color: 'var(--bq-text-tertiary, #6b6b80)',
  },
  '.cm-activeLineGutter': {
    backgroundColor: 'var(--bq-surface-hover, #2d2d44)',
  },
  '.cm-activeLine': {
    backgroundColor: 'rgba(99, 102, 241, 0.08)',
  },
  '.cm-selectionBackground': {
    backgroundColor: 'rgba(99, 102, 241, 0.3) !important',
  },
  '&.cm-focused .cm-selectionBackground': {
    backgroundColor: 'rgba(99, 102, 241, 0.3) !important',
  },
  '.cm-cursor': {
    borderLeftColor: 'var(--bq-color-primary, #6366f1)',
  },
  // Error line highlighting
  '.cm-errorLine': {
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    borderLeft: '3px solid #ef4444',
  },
  // Scrollbar styling for dark theme
  '.cm-scroller::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '.cm-scroller::-webkit-scrollbar-track': {
    background: 'var(--bq-surface-base, #1a1a2e)',
  },
  '.cm-scroller::-webkit-scrollbar-thumb': {
    background: 'var(--bq-border-strong, #4a4a6a)',
    borderRadius: '4px',
  },
  '.cm-scroller::-webkit-scrollbar-thumb:hover': {
    background: 'var(--bq-text-tertiary, #6b6b80)',
  },
});

const lightBaseTheme = EditorView.theme({
  '&': {
    backgroundColor: 'var(--bq-surface-base, #ffffff)',
    borderRadius: '6px',
    border: '1px solid var(--bq-border-muted, #e2e8f0)',
    fontSize: '13px',
    flex: '1',
    minHeight: '0',
    maxHeight: '100%',
    overflow: 'hidden',
  },
  '&.cm-editor': {
    height: '100%',
  },
  '&.cm-focused': {
    outline: 'none',
    borderColor: 'var(--bq-color-primary, #6366f1)',
  },
  '.cm-scroller': {
    fontFamily: '"JetBrains Mono", "Fira Code", "Monaco", monospace',
    lineHeight: '1.6',
    overflow: 'auto',
  },
  '.cm-gutters': {
    backgroundColor: 'var(--bq-surface-elevated, #f8fafc)',
    borderRight: '1px solid var(--bq-border-muted, #e2e8f0)',
    color: 'var(--bq-text-tertiary, #94a3b8)',
  },
  '.cm-activeLineGutter': {
    backgroundColor: 'var(--bq-surface-hover, #f1f5f9)',
  },
  '.cm-activeLine': {
    backgroundColor: 'rgba(99, 102, 241, 0.06)',
  },
  '.cm-selectionBackground': {
    backgroundColor: 'rgba(99, 102, 241, 0.2) !important',
  },
  '&.cm-focused .cm-selectionBackground': {
    backgroundColor: 'rgba(99, 102, 241, 0.2) !important',
  },
  '.cm-cursor': {
    borderLeftColor: 'var(--bq-color-primary, #6366f1)',
  },
  '.cm-content': {
    caretColor: 'var(--bq-color-primary, #6366f1)',
  },
  // Error line highlighting
  '.cm-errorLine': {
    backgroundColor: 'rgba(239, 68, 68, 0.15)',
    borderLeft: '3px solid #ef4444',
  },
  // Scrollbar styling for light theme
  '.cm-scroller::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '.cm-scroller::-webkit-scrollbar-track': {
    background: 'var(--bq-surface-elevated, #f8fafc)',
  },
  '.cm-scroller::-webkit-scrollbar-thumb': {
    background: 'var(--bq-border-strong, #cbd5e1)',
    borderRadius: '4px',
  },
  '.cm-scroller::-webkit-scrollbar-thumb:hover': {
    background: 'var(--bq-text-tertiary, #94a3b8)',
  },
});

const errorTheme = EditorView.theme({
  '&': {
    borderColor: 'var(--bq-color-danger, #ef4444) !important',
  },
});

export const YamlEditor = ({
  value,
  onChange,
  placeholder = 'Enter YAML...',
  height,
  readOnly = false,
  hasError = false,
  errorLine = null,
}: YamlEditorProps) => {
  const themeMode = useAtomValue(siteThemeMode);
  const isDark = themeMode === 'dark';
  const editorRef = useRef<ReactCodeMirrorRef>(null);

  // Update error line highlighting when errorLine changes
  useEffect(() => {
    const view = editorRef.current?.view;
    if (view) {
      view.dispatch({
        effects: setErrorLineEffect.of(errorLine),
      });
    }
  }, [errorLine]);

  const handleCreateEditor = useCallback(
    (view: EditorView) => {
      // Set initial error line if present
      if (errorLine !== null) {
        view.dispatch({
          effects: setErrorLineEffect.of(errorLine),
        });
      }
    },
    [errorLine],
  );

  const extensions = [
    yaml(),
    isDark ? darkBaseTheme : lightBaseTheme,
    EditorView.lineWrapping,
    errorLineField,
    ...(hasError ? [errorTheme] : []),
  ];

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CodeMirror
        ref={editorRef}
        value={value}
        onChange={onChange}
        extensions={extensions}
        theme={isDark ? oneDark : 'light'}
        placeholder={placeholder}
        height="100%"
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        readOnly={readOnly}
        onCreateEditor={handleCreateEditor}
        basicSetup={{
          lineNumbers: true,
          highlightActiveLineGutter: true,
          highlightActiveLine: true,
          foldGutter: true,
          dropCursor: true,
          allowMultipleSelections: true,
          indentOnInput: true,
          bracketMatching: true,
          closeBrackets: true,
          autocompletion: false,
          rectangularSelection: true,
          crosshairCursor: false,
          highlightSelectionMatches: true,
          tabSize: 2,
        }}
      />
    </div>
  );
};
